import dynamic from 'next/dynamic'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Checkbox } from '../../inputs/Checkbox/Checkbox'
import itemStyles from './comfort-and-safety.module.scss'
import { FilterDropdown } from '@/components/search/FilterDropdown'
import styles from '@/components/search/search.module.scss'
import { FILTER_SPEC } from '@/lib/constants'
import { selectFullActiveSpec, selectSpecSearchFilter } from '@/store/selectors/search'
import { SearchFilterSpec, SearchFilterSpecSubFilter } from '@/types/search'
import { Size } from '@/types/system'
import { filterClassName } from '@/lib/utilities/search'
import { isProd } from '@/lib/utilities/system'

const Icon = dynamic(() => import('@/components/styleguide/Icon'))

type ComfortAndSafetyProps = {
  isFilterOpen: boolean
  toggleFilter: () => void
  onChange: (params: {
    type: 'multi' | 'single' | 'vehiclePriceTag'
    filter: string
    value: string | number
  }) => void
}

export const ComfortAndSafety = ({
  isFilterOpen,
  toggleFilter,
  onChange,
}: ComfortAndSafetyProps) => {
  const availableSpec = useSelector(selectSpecSearchFilter)
  const selectedSpec = useSelector(selectFullActiveSpec)
  const [availableFilters, setAvailableFilters] = useState<SearchFilterSpec[]>([])
  const [selectedFilters, setSelectedFilters] = useState<SearchFilterSpec[]>([])

  useEffect(() => {
    setAvailableFilters(availableSpec.filter((spec: SearchFilterSpec) => spec.categoryId === 3))
  }, [availableSpec])

  useEffect(() => {
    setSelectedFilters(
      selectedSpec.filter(
        (spec: { id: number; name: string; category: number; filter: string }) =>
          spec.category === 3 || spec.filter === 'Heated Seats' || spec.filter === 'Leather Seats',
      ),
    )
  }, [selectedSpec])

  const onChangeSelect = ({
    event,
    name,
  }: {
    event: React.FormEvent<HTMLSelectElement>
    name: string
  }) => {
    if (event.currentTarget.value === '-1') {
      const selectedId = selectedSpec.filter(
        (spec: { id: number; name: string; category: number; filter: string }) =>
          spec.filter === name,
      )[0]

      onChange({
        type: 'multi',
        value: selectedId.id,
        filter: FILTER_SPEC,
      })
    } else {
      onChange({
        type: 'multi',
        value: parseInt(event.currentTarget.value),
        filter: FILTER_SPEC,
      })
    }
  }

  return (
    <FilterDropdown
      title="Comfort &amp; safety"
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      hasSelectedOptions={selectedFilters.length > 0}
      maximumNumberOfVisibleSelectedOptions={2}
      selectedOptions={
        selectedFilters.length > 0 ? selectedFilters.map(filter => filter.name) : ['All']
      }>
      {availableFilters.length > 0 ? (
        <div className={styles['multi-select']}>
          {availableFilters
            .sort((a, b) => !!a.subFilters - !!b.subFilters)
            .map(available => {
              if (available.subFilters) {
                return (
                  <React.Fragment key={available.id}>
                    <p className={styles['sub-filter-header']}>{available.name}</p>
                    {available.name === 'Heated Seats' ? (
                      available.subFilters.map((item: SearchFilterSpecSubFilter) => (
                        <Checkbox
                          filter={FILTER_SPEC}
                          key={item.id}
                          onChange={onChange}
                          item={item}
                          valueAttribute="id"
                          isSelected={
                            selectedFilters.filter(filter => filter.id === item.id).length > 0
                          }
                          className={!isProd() ? `search__comfort-button search__comfort-button-${filterClassName(item.name)}` : ''}
                        />
                      ))
                    ) : (
                      <div className={`${styles.select} ${itemStyles['no-margin']}`}>
                        <select
                          onChange={event => onChangeSelect({ event, name: available.name })}
                          defaultValue={-1}
                          value={
                            available.subFilters.filter(
                              i => !!selectedFilters.find(j => j.id === i.id),
                            )?.[0]?.id ?? -1
                          }
                          className={`${styles.control}${!isProd() ? ` search__comfort-select-${filterClassName(available.name)}` : ''}`}
                          name={available.name}
                          title={available.name}>
                          <option
                            value={-1}
                            label="Any"
                          />
                          {available.subFilters.map((item: SearchFilterSpecSubFilter) => {
                            return (
                              <option
                                key={item.id}
                                value={item.id}>
                                {item.name}
                              </option>
                            )
                          })}
                        </select>
                        <Icon
                          customClass={styles.icon}
                          name="ChevronDown"
                          size={Size.Medium}
                        />
                      </div>
                    )}
                  </React.Fragment>
                )
              }

              return (
                <Checkbox
                  key={available.id}
                  valueAttribute="id"
                  item={available}
                  isSelected={
                    selectedFilters.filter(filter => filter.id === available.id).length > 0
                  }
                  filter={FILTER_SPEC}
                  onChange={onChange}
                  className={!isProd() ? `search__comfort-button search__comfort-button-${filterClassName(available.name)}` : ''}
                />
              )
            })}
        </div>
      ) : (
        <p className={styles['error-text']}>
          {
            'There are no "Comfort & safety" options available based on your current filter selections'
          }
        </p>
      )}
    </FilterDropdown>
  )
}
